// The Arrange menu: the layout (Auto Arrange, Grid or Freeform: settings.autoArrange and settings.snap), the Viewport switch (settings.viewport) and Sort By (name, type, size, date added: under Auto Arrange a remembered choice, plus Manual;
// otherwise a one-time sort), where tiles dropped under Auto Arrange go in the order (dropArranged, with
// dragPreview showing it while they are dragged), and the room a drop makes on the Viewport layout's grid (dropRoom,
// with roomPreview). The orders themselves live in geometry.js (ORDERS, autoOrder).
import {
  autoLayout, autoOn, autoOrder, band, cellOrder, COLUMNS, devicePx, fitX, hasSpot, makeRoom, moveTo, ORDERS, orderSpots, packedFor, pinDrawn, placeTile,
  pageX, pageY, reading, rowOf, settle, sizeOf, sizesPerScreen, snap, snapOn, sortBy, spotOf, tilePos, viewportOn,
} from './geometry.js';
import { submenu } from './menu.js';
import { render } from './render.js';
import { API, byId, $webtop, webtop, setWebtop } from './state.js';
import { GROUPABLE, isGroup, onWebtop } from './tiles.js';
import { api, toast } from './ui.js';
import { LAYOUT_BANDS, moveTiles } from '/model.js';

// The three layouts, one in force at a time: Auto Arrange (the auto layout, settings.autoArrange), Grid (the
// adaptive layout with Snap to Grid, settings.snap, a new webtop's default) and Freeform (the adaptive layout, tiles
// exactly where they are dropped; Shift while dragging still snaps). The Viewport switch (settings.viewport) goes with
// any of them: each screen size keeps its own tile sizes, and under Grid or Freeform its own spots, those two then only
// saying where a drop lands.
const LAYOUTS = [
  { value: 'auto', label: 'Auto Arrange', title: 'Keep tiles packed from the top left, in the Sort By order', said: 'Tiles arrange themselves' },
  { value: 'grid', label: 'Grid', title: 'Tiles stay where you put them, on the grid', said: 'Tiles snap to the grid' },
  { value: 'free', label: 'Freeform', title: 'Tiles stay exactly where you put them', said: 'Tiles stay where you put them' },
];
const layoutMode = () => (autoOn() ? 'auto' : snapOn() ? 'grid' : 'free');

// Switching layouts keeps the picture: into Auto Arrange with Sort By Manual, the order the adaptive layout shows
// (reading order) is saved as order spots; out of it, the packing as drawn is saved for the adaptive layout; into
// Grid, every tile snaps to the nearest cell. Switching forgets what each screen size saved, spots and sizes, with the
// Viewport switch on or off. With it on the tile's own spots stay as they are: its Manual order reads well enough from them.
async function setLayout(o) {
  if (o.value === layoutMode()) return;
  try {
    const own = Boolean(webtop.settings.viewport);
    const keep = own ? null : autoOn() ? autoLayout(onWebtop().sort(autoOrder()))
      : o.value === 'auto' && sortBy() === 'manual' ? orderSpots(onWebtop().sort(reading)) : null;
    const clears = webtop.shortcuts.filter((s) => s.layouts).map((s) => ({ id: s.id, clear: LAYOUT_BANDS }));
    if (clears.length) await api('PATCH', `${API}/shortcuts`, clears);
    moveTiles(webtop.shortcuts, clears);
    setWebtop(await api('PATCH', API, { settings: { autoArrange: o.value === 'auto', snap: o.value === 'grid' } }));
    toast(clears.length ? `${o.said}; the screen sizes' layouts were reset` : o.said);
    if (keep) await arrangeTo((s) => keep.get(s.id));
    if (o.value === 'grid' && !own) await arrangeTo((s) => snap(s.x, s.y));
    else if (!keep) render();
  } catch (e) { toast(e); }
}

// The Viewport switch: nothing is saved by turning it either way, so each screen size's layout is there when it is back on.
async function toggleViewport() {
  try {
    setWebtop(await api('PATCH', API, { settings: { viewport: !webtop.settings.viewport } }));
    render();
  } catch (e) { toast(e); }
}

// The Sort By rows; `value` is what settings.sortBy stores (the server has the same list, SORT_BY) and the key of
// its order in ORDERS.
const SORTS = [
  { value: 'name', label: 'Name' },
  { value: 'type', label: 'Type', title: 'Widgets, groups, webtop links, then links' },
  { value: 'size', label: 'Size', title: 'Biggest first' },
  { value: 'added', label: 'Date Added', title: 'Oldest first' },
];
const MANUAL = { value: 'manual', label: 'Manual', title: 'Your own order: drag a tile to move it' };
const sortItem = (o) => ({ label: o.label, title: viewportOn() ? `${o.title ? `${o.title}. ` : ''}Starts over on every screen size` : o.title, stay: true });
// Said after every sort, since one that finds the tiles already in order changes nothing on the screen.
const sortToast = (o, moved) => `${moved ? 'Sorted' : 'Already sorted'} by ${o.label.toLowerCase()}`;

// Sort By with Auto Arrange on (settings.sortBy): the order is kept, so a new tile lands in its place. Saved spots
// only matter on Manual, so only switching to it saves the order shown so far (order spots), keeping the picture.
async function setSort(o) {
  if (o.value === sortBy()) return;
  try {
    const before = onWebtop().sort(autoOrder()).map((s) => s.id).join();
    const keep = o === MANUAL ? orderSpots(onWebtop().sort(autoOrder())) : null;
    setWebtop(await api('PATCH', API, { settings: { sortBy: o.value } }));
    if (keep) { if (await arrangeTo((s) => keep.get(s.id)) === null) return; } else render();
    const after = onWebtop().sort(autoOrder()).map((s) => s.id).join();
    toast(o === MANUAL ? 'Manual order: drag tiles to move them' : sortToast(o, before !== after));
  } catch (e) { toast(e); }
}

// A one-time sort (Auto Arrange off): the tiles packed in that order like the auto layout, and the packing saved. Under
// Viewport it starts over on every screen size, whichever it ran on: what each saved, spots and sizes, is forgotten, and
// each gets the order packed on its own columns, so it reads the same everywhere.
const SORT_BATCH = 2000; // entries in one request, well inside the JSON body limit; a big webtop's sort has several per tile
async function sortEverywhere(o) {
  // Cleared before the packing is worked out: a size saved for this screen would otherwise shape it.
  const clears = webtop.shortcuts.filter((s) => s.layouts).map((s) => ({ id: s.id, clear: LAYOUT_BANDS }));
  moveTiles(webtop.shortcuts, clears);
  const list = onWebtop().sort(ORDERS[o.value]);
  const spots = [];
  for (const b of [...LAYOUT_BANDS, 'l']) {
    const packed = b === band() ? autoLayout(list) : packedFor(list, COLUMNS[b]);
    for (const s of list) spots.push({ id: s.id, ...packed.get(s.id), ...(b !== 'l' && { band: b }) });
  }
  moveTiles(webtop.shortcuts, spots);
  render();
  const updates = [...clears, ...spots];
  try {
    for (let i = 0; i < updates.length; i += SORT_BATCH) await api('PATCH', `${API}/shortcuts`, updates.slice(i, i + SORT_BATCH));
  } catch (e) { return toast(e); }
  toast(sortToast(o, true)); // starting over always changes something: the other screen sizes at least
}
async function sortOnce(o) {
  if (viewportOn()) return sortEverywhere(o);
  const packed = autoLayout(onWebtop().sort(ORDERS[o.value]));
  const moved = await arrangeTo((s) => packed.get(s.id));
  if (moved !== null) toast(sortToast(o, moved));
}

// Submenu, like Windows' View and Sort By: the layout, then the sorts, and every entry keeps the menu open. Under
// Auto Arrange, Sort By is a choice that is remembered (Manual: the order tiles are dropped in); otherwise each
// entry sorts once.
export function arrangeMenu() {
  submenu(() => [
    { label: 'Arrange', cls: 'head' },
    ...LAYOUTS.map((o) => ({ label: o.label, title: o.title, radio: true, checked: layoutMode() === o.value, stay: true, action: () => setLayout(o) })),
    {
      label: 'Viewport', checked: sizesPerScreen(), stay: true, action: toggleViewport,
      title: autoOn() ? 'Small, medium, wide and large screens each keep their own tile sizes' : 'Small, medium, wide and large screens each keep their own tile positions and sizes',
    },
    'sep',
    { label: 'Sort By', cls: 'head' },
    ...(autoOn()
      ? [MANUAL, ...SORTS].map((o) => ({ ...sortItem(o), radio: true, checked: sortBy() === o.value, action: () => setSort(o) }))
      : SORTS.map((o) => ({ ...sortItem(o), action: () => sortOnce(o) }))),
  ]);
}

// Auto Arrange: the order once tiles dragged among the others (`moved`, id -> where each is) are dropped there. They
// take their place in the order as drawn, like a tile dragged in a list: the row nearest the dragged tile's top
// edge, and in it after every tile whose middle it was dragged past, so one dropped between two tiles lands between
// them, and one dropped onto a tile takes its place only beyond its middle.
function dropOrder(moved) {
  const drawn = autoLayout(onWebtop().sort(cellOrder));
  const spot = (s) => moved.get(s.id) ?? drawn.get(s.id);
  const middle = (s) => spot(s).x + sizeOf(s).w / 2;
  return onWebtop().sort((a, b) => rowOf(spot(a)) - rowOf(spot(b)) || middle(a) - middle(b) || moved.has(b.id) - moved.has(a.id));
}
// Tiles dropped under Auto Arrange: the new order is saved as order spots. On a sorted webtop the sort decides: the
// tiles go back to their places.
export function dropArranged(moved) {
  if (sortBy() !== 'manual') return render();
  const order = orderSpots(dropOrder(moved));
  return arrangeTo((s) => order.get(s.id));
}

// While tiles are dragged under Auto Arrange (Manual), the other tiles slide aside to where the drop would put them
// (dropOrder), and a marker sits in each gap. Runs on every pointer move, so it redraws only when the order changes.
let previewed = null; // the ids of the order last drawn, joined
export function dragPreview(moved) {
  if (!autoOn() || sortBy() !== 'manual') return;
  const list = dropOrder(moved);
  const key = list.map((s) => s.id).join();
  if (key === previewed) return;
  previewed = key;
  const pos = autoLayout(list);
  $webtop.classList.add('previewing');
  $webtop.querySelectorAll('.drop-target').forEach((el) => el.remove());
  for (const s of list) {
    if (!moved.has(s.id)) {
      const el = $webtop.querySelector(`.tile[data-id="${s.id}"]`);
      if (el) placeTile(el, pos.get(s.id));
      continue;
    }
    markGap(sizeOf(byId(s.id)), pos.get(s.id));
  }
}
function markGap(size, p) {
  const mark = Object.assign(document.createElement('div'), { className: 'drop-target' });
  mark.style.width = `${devicePx(size.w * fitX())}px`;
  mark.style.height = `${devicePx(size.h)}px`;
  placeTile(mark, p);
  $webtop.prepend(mark); // under the tiles, so one sliding across it passes over it
}

// The same on the Viewport layout's grid: while tiles are dragged, the ones in the way slide forward and each dragged
// tile's cell is marked. `base` holds where the others were drawn before the drag, which every preview starts from and a
// drag that ends puts back, and `baseGroups` the groups among them: a drop on one joins it, so it is left where it was
// rather than sliding out from under the pointer. `previewed` is the cells last drawn for.
let base = null;
let baseGroups = null;
const others = (moved) => [...$webtop.querySelectorAll('.tile[data-id]')].filter((el) => !moved.has(el.dataset.id));
function startRoom(moved) {
  if (base) return;
  base = new Map(others(moved).map((el) => [el, tilePos(el)]));
  baseGroups = [...base].flatMap(([el, p]) => { const s = byId(el.dataset.id); return isGroup(s) ? [{ ...p, ...sizeOf(s) }] : []; });
}
// The cells the dragged tiles take, each clear of the ones before it.
function dropSpots(moved) {
  const spots = new Map();
  const rects = [];
  for (const [id, at] of [...moved].sort((a, b) => reading(a[1], b[1]))) {
    const size = sizeOf(byId(id));
    const p = settle(snap(at.x, at.y, size, false, true), rects, size);
    spots.set(id, p);
    rects.push({ ...p, ...size });
  }
  return { spots, rects };
}
// The other tiles as rects, where they were before the drag.
const restRects = (moved) => others(moved).map((el) => ({ id: el.dataset.id, ...(base?.get(el) ?? tilePos(el)), ...sizeOf(byId(el.dataset.id)) }));
// `idle`: nothing is being aimed at (the pointer over the trash). No `pointer`: a resize, which joins no group and needs
// no marker, the tile itself being drawn at the size and spot it will take.
export function roomPreview(moved, pointer, idle) {
  startRoom(moved);
  const px = pointer && pageX(pointer.clientX), py = pointer && pageY(pointer.clientY);
  const joins = pointer && baseGroups.some((r) => px >= r.x && px <= r.x + r.w && py >= r.y && py <= r.y + r.h)
    && [...moved.keys()].every((id) => GROUPABLE.includes(byId(id).kind));
  const { spots, rects } = idle || joins ? { spots: new Map(), rects: null } : dropSpots(moved);
  // The slide follows the cells and sizes alone, so only a move into another one works it out and redraws.
  const key = JSON.stringify([...spots].map(([id, p]) => [id, p, sizeOf(byId(id))]));
  if (key === previewed) return;
  previewed = key;
  const shifted = rects ? makeRoom(rects, restRects(moved)) : new Map();
  $webtop.classList.add('previewing');
  $webtop.querySelectorAll('.drop-target').forEach((el) => el.remove());
  for (const [el, p] of base) placeTile(el, shifted.get(el.dataset.id) ?? p);
  if (pointer) for (const [id, p] of spots) markGap(sizeOf(byId(id)), p);
}
// Saves a drop on the Viewport layout's grid. The previews have ended, so the others are back where they were.
export async function dropRoom(moved) {
  const { spots, rects } = dropSpots(moved);
  const shifted = makeRoom(rects, restRects(moved));
  const updates = [...spots, ...shifted].map(([id, p]) => moveTo(byId(id), p));
  updates.push(...pinDrawn([...spots.keys(), ...shifted.keys()].map(byId)));
  render();
  try { await api('PATCH', `${API}/shortcuts`, updates); } catch (e) { toast(e); }
}
export function endPreview() {
  previewed = null;
  if (base) for (const [el, p] of base) placeTile(el, p);
  base = baseGroups = null;
  $webtop.classList.remove('previewing');
  $webtop.querySelectorAll('.drop-target').forEach((el) => el.remove());
}

// Moves tiles to fn(s) and saves them: true when any moved, false when none did, null when the save failed (shown).
async function arrangeTo(fn) {
  const updates = [];
  for (const s of webtop.shortcuts) {
    const p = fn(s);
    const at = spotOf(s);
    if (p && (!hasSpot(s) || p.x !== at.x || p.y !== at.y)) updates.push(moveTo(s, p));
  }
  render();
  if (!updates.length) return false;
  try { await api('PATCH', `${API}/shortcuts`, updates); } catch (e) { toast(e); return null; }
  return true;
}
